import * as XLSX from 'xlsx';
import type { C1PlacementRow } from '@type/store';

type SheetCell = string | number | boolean | null | undefined;
type SheetRow = SheetCell[];
type SheetRows = SheetRow[];

export const PLACEMENT_EMPLOYMENT_STATUS_OPTIONS = [
  'Employed in relevant industry',
  'Employed in irrelevant industry',
  'Self Employed',
  'Continue Education',
  'Not Employed',
];

export const PLACEMENT_PROFORMA_DISPLAY_HEADERS = [
  'Sr. No',
  'District',
  'Name of Institute',
  'Name of course',
  'Duration',
  'Start Date',
  'End date',
  'Name of trainee',
  'Gender M/F',
  'Contact No. of trainee',
  'CNIC No.',
  'Employment Status',
  'Name of Industry/Company',
  'Monthly Salary/Income',
  'Designation',
  'Contact No. of Industry/Company',
  'Address of the Industry/Company',
];

export const PLACEMENT_PROFORMA_HEADERS = [
  'Sr. No',
  'District',
  'Name of Institute',
  'Name of course',
  'Duration',
  'Start Date',
  'End date',
  'Name of trainee',
  'Gender M/F',
  'Contact No. of trainee',
  'CNIC No.',
  'Employment Status\r\n1. Employed in relevant industry\r\n2. Employed in irrelevant industry\r\n3. Self Employed\r\n4. Continue Education\r\n5.Not Employed',
  'Name of Industry/\r\nCompany (Where Trainee employed)',
  'Monthly Salary/Income',
  'Designation',
  'Contact No. of Industry/Company',
  'Address of the Industry/Company',
];

function normCell(s: unknown): string {
  return String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** First line of merged / multi-line Excel header cells. */
function normHeaderCell(s: unknown): string {
  return String(s ?? '').split(/[\r\n]+/)[0].trim().toLowerCase().replace(/\s+/g, ' ');
}

export function normalizePlacementEmploymentStatus(raw: unknown): string {
  const s = String(raw ?? '').trim().replace(/\s+/g, ' ');
  if (!s) return '';
  const lower = s.toLowerCase();

  if (/^1\.?\s*$|^1[\.)]/.test(lower) || (lower.includes('relevant') && !lower.includes('irrelevant'))) {
    return PLACEMENT_EMPLOYMENT_STATUS_OPTIONS[0];
  }
  if (/^2\.?\s*$|^2[\.)]/.test(lower) || lower.includes('irrelevant')) {
    return PLACEMENT_EMPLOYMENT_STATUS_OPTIONS[1];
  }
  if (/^3\.?\s*$|^3[\.)]/.test(lower) || /self[\s-]*employ/.test(lower)) {
    return PLACEMENT_EMPLOYMENT_STATUS_OPTIONS[2];
  }
  if (/^4\.?\s*$|^4[\.)]/.test(lower) || /continue[\s-]*educ/.test(lower)) {
    return PLACEMENT_EMPLOYMENT_STATUS_OPTIONS[3];
  }
  if (/^5\.?\s*$|^5[\.)]/.test(lower) || /not[\s-]*employ/.test(lower)) {
    return PLACEMENT_EMPLOYMENT_STATUS_OPTIONS[4];
  }

  const exact = PLACEMENT_EMPLOYMENT_STATUS_OPTIONS.find((opt) => opt.toLowerCase() === lower);
  if (exact) return exact;

  return s;
}

function findPlacementHeaderIndex(sheetRows: SheetRows): number {
  for (let i = 0; i < Math.min(sheetRows.length, 20); i++) {
    const line = (sheetRows[i] || []).map((c) => normCell(c)).join(' ');
    if (line.includes('name of trainee') && line.includes('name of institute')) return i;
  }
  return 0;
}

function colIdx(hdr: string[], patterns: string[], exact = false): number {
  for (const pat of patterns) {
    const idx = hdr.findIndex((h) => (exact ? h === pat : h.includes(pat)));
    if (idx >= 0) return idx;
  }
  return -1;
}

function pick(row: SheetRow, hdr: string[], patterns: string[], exact = false): string {
  const i = colIdx(hdr, patterns, exact);
  if (i < 0 || row[i] == null || String(row[i]).trim() === '') return '';
  return String(row[i]).trim();
}

export function parsePlacementProformaRows(sheetRows: SheetRows): C1PlacementRow[] {
  if (!sheetRows?.length) return [];
  const headerIdx = findPlacementHeaderIndex(sheetRows);
  const hdr = (sheetRows[headerIdx] || []).map((c) => normHeaderCell(c));
  const rows: C1PlacementRow[] = [];

  for (let i = headerIdx + 1; i < sheetRows.length; i++) {
    const row = sheetRows[i] || [];
    const traineeName = pick(row, hdr, ['name of trainee']);
    const inst = pick(row, hdr, ['name of institute', 'institute']);
    const course = pick(row, hdr, ['name of course', 'course']);
    const dist = pick(row, hdr, ['district']);
    if (!traineeName && !inst && !course && !dist) continue;

    rows.push({
      sr: rows.length + 1,
      dist,
      inst,
      course,
      dur: pick(row, hdr, ['duration']),
      start: pick(row, hdr, ['start date']),
      end: pick(row, hdr, ['end date', 'end']),
      traineeName,
      gender: pick(row, hdr, ['gender']),
      traineeContact: pick(row, hdr, ['contact no. of trainee', 'contact no of trainee', 'trainee contact']),
      cnic: pick(row, hdr, ['cnic']),
      employmentStatus: normalizePlacementEmploymentStatus(
        pick(row, hdr, ['employment status'], true),
      ),
      employerName: pick(row, hdr, ['name of industry', 'company (where trainee']),
      salary: pick(row, hdr, ['monthly salary', 'salary/income']),
      designation: pick(row, hdr, ['designation']),
      employerContact: pick(row, hdr, ['contact no. of industry', 'contact no of industry']),
      employerAddress: pick(row, hdr, ['address of the industry', 'address of industry']),
    });
  }
  return rows;
}

export async function importPlacementProformaFile(file: File): Promise<C1PlacementRow[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  let best: C1PlacementRow[] = [];
  for (const name of wb.SheetNames) {
    const sheetRows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '' }) as SheetRows;
    const parsed = parsePlacementProformaRows(sheetRows);
    if (parsed.length > best.length) best = parsed;
  }
  return best;
}

export function placementRowsToSheet(rows: C1PlacementRow[]): (string | number)[][] {
  const data: (string | number)[][] = [PLACEMENT_PROFORMA_HEADERS];
  rows.forEach((r, i) => {
    data.push([
      r.sr ?? i + 1,
      r.dist ?? '',
      r.inst ?? '',
      r.course ?? '',
      r.dur ?? '',
      r.start ?? '',
      r.end ?? '',
      r.traineeName ?? '',
      r.gender ?? '',
      r.traineeContact ?? '',
      r.cnic ?? '',
      r.employmentStatus ?? '',
      r.employerName ?? '',
      r.salary ?? '',
      r.designation ?? '',
      r.employerContact ?? '',
      r.employerAddress ?? '',
    ]);
  });
  return data;
}

export function exportPlacementExcel(rows: C1PlacementRow[], filename = 'Component-I-Placement-Proforma.xlsx'): void {
  const ws = XLSX.utils.aoa_to_sheet(placementRowsToSheet(rows));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Placement');
  XLSX.writeFile(wb, filename);
}

function escHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function exportPlacementPdf(
  rows: C1PlacementRow[],
  title = 'Component I — Placement Proforma',
  filterLabels: string[] = [],
): boolean {
  const headers = [
    'Sr.', 'District', 'Institute', 'Course', 'Duration', 'Start', 'End',
    'Trainee', 'Gender', 'Contact', 'CNIC', 'Employment Status', 'Employer',
    'Salary', 'Designation', 'Employer Contact', 'Employer Address',
  ];
  const head = headers.map((h) => `<th>${escHtml(h)}</th>`).join('');
  const body = rows.map((r, i) => {
    const cells = [
      r.sr ?? i + 1,
      r.dist, r.inst, r.course, r.dur, r.start, r.end,
      r.traineeName, r.gender, r.traineeContact, r.cnic, r.employmentStatus,
      r.employerName, r.salary, r.designation, r.employerContact, r.employerAddress,
    ];
    return `<tr>${cells.map((c) => `<td>${escHtml(String(c ?? ''))}</td>`).join('')}</tr>`;
  }).join('');

  const filterHtml = filterLabels.length
    ? `<div class="filters"><strong>Filters:</strong> ${filterLabels.map((label) => `<span class="chip">${escHtml(label)}</span>`).join(' ')}</div>`
    : '';

  const html = `<!DOCTYPE html><html><head><title>${escHtml(title)}</title>
<style>
  body { font-family: Inter, Arial, sans-serif; font-size: 9px; padding: 16px; color: #1a1a2e; }
  h1 { font-size: 16px; margin: 0 0 4px; }
  .meta { color: #6b7280; margin-bottom: 14px; font-size: 11px; }
  .filters { margin-bottom: 12px; line-height: 1.6; }
  .chip { display: inline-block; background: #EFF6FF; color: #1E40AF; border-radius: 999px; padding: 3px 8px; margin: 2px 4px 2px 0; font-size: 10px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #d1d5db; padding: 4px 5px; text-align: left; vertical-align: top; }
  th { background: #026cb5; color: #fff; font-size: 8px; }
  tr:nth-child(even) td { background: #f9fafb; }
</style></head><body>
  <h1>${escHtml(title)}</h1>
  <div class="meta">${rows.length} trainee records · Exported ${new Date().toLocaleString('en-PK')}</div>
  ${filterHtml}
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;

  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
  return true;
}
